import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Upload, X, Loader2, CalendarDays, Sparkles, Send, Check } from "lucide-react";
import { submitMoodBoard } from "@/lib/apiClient";
import { fileToDataUrl } from "@/lib/imageUtils";

/**
 * Mood-board submission form. Reused from post-application congrats and /free-shoot.
 * Applicant must already exist server-side; caller supplies applicationId + email.
 */
export default function MoodBoardForm({
  applicationId,
  email,
  applicantName = "",
  onSuccess,
  className = "",
}) {
  const [images, setImages] = useState([]);
  const [dates, setDates] = useState("");
  const [style, setStyle] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  const addFiles = async (list) => {
    const files = Array.from(list || []);
    const room = 6 - images.length;
    if (room <= 0) {
      toast.error("Maximum of 6 mood board images.");
      return;
    }
    try {
      const encoded = await Promise.all(files.slice(0, room).map((f) => fileToDataUrl(f)));
      setImages((prev) => [...prev, ...encoded]);
    } catch {
      toast.error("Could not read one of the images.");
    }
  };

  const submit = async () => {
    if (images.length === 0) {
      toast.error("Add at least one mood-board reference.");
      return;
    }
    setSubmitting(true);
    try {
      const preferred_dates = dates
        .split(/[,\n]/)
        .map((d) => d.trim())
        .filter(Boolean);
      await submitMoodBoard(applicationId, {
        email,
        images,
        preferred_dates,
        shoot_style: style || null,
        notes: notes || null,
      });
      setSubmitted(true);
      toast.success("Mood board submitted!");
      onSuccess?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        data-testid="moodboard-success"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`text-center py-10 ${className}`}
      >
        <div className="w-16 h-16 rounded-full bg-white text-black flex items-center justify-center mx-auto mb-6">
          <Check size={28} strokeWidth={2} />
        </div>
        <h3 className="font-display text-3xl md:text-4xl font-light tracking-tight text-white mb-4">
          Locked in{applicantName ? `, ${applicantName.split(" ")[0]}` : ""}.
        </h3>
        <p className="text-white/60 leading-relaxed max-w-md mx-auto">
          Your mood board is with our creative team. If approved, you&rsquo;ll receive an email confirming your free shoot date within a few days.
        </p>
      </motion.div>
    );
  }

  return (
    <div data-testid="moodboard-form" className={`space-y-8 ${className}`}>
      {/* Image grid */}
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 block mb-3">
          Mood board references
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {images.map((src, i) => (
            <div key={`${src.slice(-24)}-${i}`} className="relative aspect-[3/4] overflow-hidden rounded-xl">
              <img src={src} alt={`mood ${i}`} className="w-full h-full object-cover" />
              <button
                type="button"
                data-testid={`mb-remove-${i}`}
                onClick={() => setImages((all) => all.filter((_, idx) => idx !== i))}
                className="absolute top-1.5 right-1.5 bg-black/70 rounded-full p-1"
                aria-label="Remove"
              >
                <X size={12} className="text-white" />
              </button>
            </div>
          ))}
          {images.length < 6 && (
            <button
              type="button"
              data-testid="mb-add-btn"
              onClick={() => fileRef.current?.click()}
              className="aspect-[3/4] rounded-xl border border-dashed border-white/25 flex flex-col items-center justify-center gap-1.5 text-white/50 active:scale-[0.98] transition-transform"
            >
              <Upload size={18} strokeWidth={1.5} />
              <span className="text-[10px] uppercase tracking-[0.2em]">Add</span>
            </button>
          )}
        </div>
        <input
          ref={fileRef}
          data-testid="mb-file-input"
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            if (fileRef.current) fileRef.current.value = "";
          }}
        />
        <p className="text-[10px] uppercase tracking-[0.2em] text-white/30 mt-3">
          Up to 6 references. Screenshots, tearsheets, campaign frames all welcome.
        </p>
      </div>

      {/* Shoot style */}
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 flex items-center gap-2 mb-3">
          <Sparkles size={12} /> Shoot style
        </label>
        <input
          data-testid="mb-style"
          value={style}
          onChange={(e) => setStyle(e.target.value)}
          placeholder="Editorial · Beauty · High-fashion · Portrait…"
          className="w-full bg-transparent border-0 border-b border-white/25 text-lg font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3"
        />
      </div>

      {/* Available dates */}
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 flex items-center gap-2 mb-3">
          <CalendarDays size={12} /> Dates you&rsquo;re free
        </label>
        <textarea
          data-testid="mb-dates"
          value={dates}
          onChange={(e) => setDates(e.target.value)}
          rows={2}
          placeholder="e.g. Sep 20, Sep 27, first week of October"
          className="w-full bg-transparent border-0 border-b border-white/25 text-base font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white pb-3 resize-none"
        />
      </div>

      {/* Notes */}
      <div>
        <label className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/50 block mb-3">
          Anything else we should know?
        </label>
        <textarea
          data-testid="mb-notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="City preference, indoor / outdoor, references you love…"
          className="w-full bg-transparent border border-white/20 rounded-2xl p-4 text-base font-body text-white placeholder:text-white/25 focus:outline-none focus:border-white resize-none"
        />
      </div>

      <button
        type="button"
        data-testid="mb-submit"
        onClick={submit}
        disabled={submitting}
        className="w-full flex items-center justify-between rounded-full bg-white text-black px-7 py-4 text-xs uppercase tracking-[0.25em] font-bold active:scale-[0.98] transition-transform disabled:opacity-60"
      >
        {submitting ? (
          <><Loader2 size={16} className="animate-spin" /> Sending…<span /></>
        ) : (
          <>Submit mood board <Send size={14} /></>
        )}
      </button>

      <p className="text-[10px] uppercase tracking-[0.25em] text-white/30 leading-relaxed">
        If approved, our team will email you with shoot dates, location and call sheet. Approval is not guaranteed.
      </p>
    </div>
  );
}
